'use strict';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';

const TEXT_FIELD_SX = {
	'& .MuiOutlinedInput-root': {
		height: 'auto',
		minHeight: 56,
		padding: 0,
		alignItems: 'center',
	},
	'& .MuiOutlinedInput-input': {
		py: 1.5,
		px: 1.75,
		height: 'auto',
		boxSizing: 'border-box',
	},
};

export const PendingApprovalAddNamesModal = ({
	open = false,
	pendingUser = null,
	onCancel,
	onSubmit,
	isSubmitting = false,
}) => {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');

	useEffect(() => {
		if (!open) return;
		setFirstName(pendingUser?.firstName?.trim() || '');
		setLastName(pendingUser?.lastName?.trim() || '');
	}, [open, pendingUser]);

	const errors = useMemo(() => ({
		firstName: firstName.trim() ? '' : 'First name is required',
		lastName: lastName.trim() ? '' : 'Last name is required',
	}), [firstName, lastName]);

	const isValid = !errors.firstName && !errors.lastName;

	const handleSubmit = useCallback(() => {
		if (!isValid || isSubmitting) return;
		onSubmit({
			...pendingUser,
			firstName: firstName.trim(),
			lastName: lastName.trim(),
		});
	}, [firstName, isSubmitting, isValid, lastName, onSubmit, pendingUser]);

	return (
		<Dialog open={open} onClose={isSubmitting ? undefined : onCancel} maxWidth="sm" fullWidth>
			<DialogTitle>Enter user names</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					<TextField
						label="First Name"
						value={firstName}
						onChange={(event) => setFirstName(event.target.value)}
						required
						fullWidth
						sx={TEXT_FIELD_SX}
						error={!!errors.firstName}
						helperText={errors.firstName || ' '}
					/>
					<TextField
						label="Last Name"
						value={lastName}
						onChange={(event) => setLastName(event.target.value)}
						required
						fullWidth
						sx={TEXT_FIELD_SX}
						error={!!errors.lastName}
						helperText={errors.lastName || ' '}
					/>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
				<Button variant="contained" onClick={handleSubmit} disabled={!isValid || isSubmitting}>
					{isSubmitting ? 'Approving…' : 'Approve'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

PendingApprovalAddNamesModal.propTypes = {
	open: PropTypes.bool,
	pendingUser: PropTypes.shape({
		email: PropTypes.string,
		firstName: PropTypes.string,
		lastName: PropTypes.string,
	}),
	onCancel: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool,
};

export default PendingApprovalAddNamesModal;
